import { useMemo, useState } from "react";
import { ORDER_TYPES } from "../../../../consts/api";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { uiNotification } from "../../../../services/UINotificationService";
import { useDeletedProcedureNotesQuery } from "../../../../api/deletedNotes/useDeletedProcedureNotesQuery";

const prepareReqOrder = (orderState) => {
  if (Object.values(orderState).every(Boolean)) {
    return {
      by: orderState.by,
      direction: orderState.direction,
    };
  }
  return null;
};

const prepareReqPeriod = (periodState) => {
  return {
    from: periodState.from || null,
    to: periodState.to || null,
  };
};

export function useProcedureNotes({ isActive, searchTerm, period }) {
  const { tSettings } = useAppTranslation.Settings();

  const [order, setOrder] = useState({
    by: "",
    direction: "",
  });

  const updateOrder = (orderFieldKey) => {
    setOrder({
      by: orderFieldKey,
      direction:
        order.direction === ORDER_TYPES.desc
          ? ORDER_TYPES.asc
          : ORDER_TYPES.desc,
    });
  };

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDeletedProcedureNotesQuery({
    payload: {
      pageSize: 15,
      period: prepareReqPeriod(period),
      order: prepareReqOrder(order),
      search: searchTerm,
    },
    options: {
      enabled: isActive,
      staleTime: 0,
      cacheTime: 0,
      keepPreviousData: true,
      onError: () => {
        uiNotification.error(tSettings("deletedNotes.procedure.error.fetch"));
      },
    },
  });

  const notes = useMemo(() => {
    if (data?.pages) {
      return data.pages.map((p) => p.data).reduce((r, p) => [...r, ...p], []);
    }
    return [];
  }, [data?.pages]);

  const isOrderActive = Object.values(order).some(Boolean);
  const isPeriodActive = Object.values(period).some(Boolean);

  const isSoftLoading =
    (isOrderActive || isPeriodActive) &&
    isFetching &&
    !isLoading &&
    !isFetchingNextPage;

  return {
    data: {
      value: notes,
      isLoading,
      isSoftLoading,
      hasMore: Boolean(hasNextPage),
      isNextFetching: isFetchingNextPage,
      fetchMore: fetchNextPage,
    },
    order: {
      value: order,
      onChange: updateOrder,
    },
  };
}
